import { urlConfig, urlControlPanel } from '@/urls';
import { pointOfSaleApi } from '@repo/api-client';
import { PointOfSale } from '@repo/shared-types';
import { LoaderFunctionArgs, useLoaderData, useRevalidator } from 'react-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/components/dialog';
import { Button } from '@repo/ui/components/button';
import { PencilIcon, PlusIcon, QrCodeIcon } from 'lucide-react';
import { CreatePosForm } from '@/components/pos/create-pos-form';
import QRCode from 'react-qr-code';
import { NotOkStatusIndicator, OkStatusIndicator } from '@repo/ui/components/status-indicator';
import { useCurrentClub } from '@/hooks/use-current-club';
import { useState } from 'react';
import { DataTable } from '@repo/ui/components/data-table';
import { DataTableRowActions } from '@repo/ui/components/data-table-row-actions';

function ViewQR({ token }: { token: string }) {
  return (
    <Dialog>
      <DialogTrigger>
        <Button variant="outline">
          <QrCodeIcon className="w-4 h-4" />
          Ver Token
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>QR Token</DialogTitle>
        <div className="flex flex-col items-center justify-center py-4">
          <QRCode value={token} size={180} />
          <span className="mt-4 break-all text-xs">{token}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const columns = (revalidate: () => void, clubId: string, hasPlayersFeature: boolean) => {
  const baseColumns = [
    {
      header: 'Nombre',
      accessorKey: 'name',
    },
    {
      header: 'Descripción',
      accessorKey: 'description',
    },
    {
      header: 'Token',
      accessorKey: 'token',
      cell: ({ row }: { row: { original: PointOfSale } }) => {
        return <ViewQR token={row.original.token} />;
      },
    },
    {
      header: 'Token Club',
      accessorKey: 'clubToken',
      cell: ({ row }: { row: { original: PointOfSale } }) => {
        return <ViewQR token={row.original.clubToken} />;
      },
    },
  ];
  const playerRegistrationColumn = hasPlayersFeature
    ? [
        {
          header: 'Registro de jugadores',
          accessorKey: 'enablePlayerRegistrationIndicator',
          cell: ({ row }: { row: { original: PointOfSale } }) => {
            return row.original.enablePlayerRegistration ? (
              <OkStatusIndicator />
            ) : (
              <NotOkStatusIndicator />
            );
          },
        },
        {
          header: 'Acciones',
          accessorKey: 'actions',
          cell: ({ row }: { row: { original: PointOfSale } }) => {
            return (
              <DataTableRowActions
                actions={[
                  {
                    label: row.original.enablePlayerRegistration
                      ? 'Desactivar registro'
                      : 'Activar registro',
                    onClick: async () => {
                      await pointOfSaleApi.updatePointOfSale(clubId, row.original.id, {
                        enablePlayerRegistration: !row.original.enablePlayerRegistration,
                      });
                      revalidate();
                    },
                    icon: <PencilIcon className="w-4 h-4" />,
                  },
                ]}
              />
            );
          },
        },
      ]
    : [];
  return [...baseColumns, ...playerRegistrationColumn];
};

function Pos() {
  const club = useCurrentClub();
  const data = useLoaderData<typeof route.loader>();
  const revalidator = useRevalidator();
  const [open, setOpen] = useState(false);

  const createDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button>
          <PlusIcon className="w-4 h-4" />
          Crear punto de venta
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Crear punto de venta</DialogTitle>
        <DialogDescription>Crea un nuevo punto de venta para tu club.</DialogDescription>
        <CreatePosForm onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );

  const tableData = data.results.filter((p) => p.clubToken !== null);

  return (
    <DataTable
      total={tableData.length}
      columns={columns(revalidator.revalidate, club.id, club.features?.players ?? false)}
      data={tableData}
      mainActions={() => createDialog}
    />
  );
}

export const route = {
  index: true,
  path: urlConfig(':id', 'pos'),
  element: <Pos />,
  handle: {
    title: 'Gestión de los puntos de venta',
    backLink: (params: { id: string }) => urlControlPanel(params.id),
  },
  loader: ({ params }: LoaderFunctionArgs) => {
    return pointOfSaleApi.getAll(params.id as string);
  },
};
